import React from 'react';
    import { motion } from 'framer-motion';
    import * as FiIcons from 'react-icons/fi';
    import SafeIcon from '../common/SafeIcon';
    import Navigation from '../components/Navigation';
    import CosmicBackground from '../components/CosmicBackground';

    const { FiLink, FiArrowRight, FiCheck, FiStar, FiUsers } = FiIcons;

    const LinkInBioProPage = () => {
      const handleProLinkClick = () => {
        window.open('https://link.fastpaydirect.com/payment-link/68f0315a67ee3bf26569ef0a', '_blank');
      };

      const pathwaySteps = [
        { step: '1', title: 'Leverage Current Affiliates', description: 'Continue earning from your existing partnerships while you learn our systems' },
        { step: '2', title: 'Learn Our Software', description: 'Use Link in Bio Pro and Lumadious to streamline your current affiliate operations' },
        { step: '3', title: 'Build Your Own Program', description: 'Learn to recruit,manage,and automate your own affiliate network' },
        { step: '4', title: 'Scale & Automate', description: 'Run a fully automated affiliate empire using our proven systems' },
      ];

      const learningModules = [
        { title: 'Affiliate Link Management', description: 'Organize and optimize all your current affiliate links in Link in Bio Pro', tools: 'Link in Bio Pro' },
        { title: 'Performance Analytics', description: 'Track which partnerships perform best and double down on winners', tools: 'Lumadious Analytics' },
        { title: 'Automated Recruitment', description: 'Build systems to attract and onboard your own affiliate partners', tools: 'Lumadious Workflows' },
        { title: 'Commission Management', description: 'Automate payments and tracking for your affiliate program', tools: 'Lumadious Automation' },
      ];

      const successStories = [
        { name: 'Sarah M.', role: 'Content Creator', current: '$8,400/month from existing affiliates', journey: 'Used our systems to build her own affiliate program with 150+ partners', outcome: 'Now earning $24,000/month total', timeframe: '8 months' },
        { name: 'Marcus R.', role: 'Course Creator', current: '$12,600/month from partnerships', journey: 'Leveraged Link in Bio Pro to streamline operations,then built own network', outcome: 'Manages 300+ affiliates automatically', timeframe: '12 months' },
      ];

      const benefits = [
        'Everything from Clarity Club included',
        'Full access to all 45+ premium Prompt Tools',
        'Professional branded link pages',
        'Advanced analytics and tracking',
        'A/B testing for optimization',
        'Custom domain support',
        'Comprehensive onboarding process',
        'Learn to build your own affiliate network',
      ];

      return (
        <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
          <CosmicBackground />
          <Navigation />

          {/* Hero Section */}
          <section className="pt-32 pb-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center max-w-4xl mx-auto"
              >
                <div className="inline-flex items-center space-x-2 bg-purple-900/30 px-4 py-2 rounded-full border border-purple-500/30 mb-6">
                  <SafeIcon icon={FiLink} className="text-purple-400" />
                  <span className="text-sm font-poppins text-purple-200">Most Popular Choice</span>
                </div>
                <h1 className="text-5xl md:text-7xl font-orbitron font-bold mb-6 leading-tight">
                  <span className="clarity-gradient">Link in Bio Pro</span>
                </h1>
                <p className="text-xl md:text-2xl text-gray-300 mb-8 font-poppins font-light max-w-3xl mx-auto leading-relaxed">
                  The perfect bridge between starting out and full automation. Leverage your current affiliate partnerships to fund learning our systems and build your own affiliate empire.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-12">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleProLinkClick}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 px-8 py-4 rounded-full font-poppins font-semibold text-lg glow-button flex items-center space-x-2"
                  >
                    <span>Get Started for $33/month</span>
                    <SafeIcon icon={FiArrowRight} />
                  </motion.button>
                  <div className="text-center">
                    <p className="text-gray-400 font-poppins text-sm">Complete setup included • Cancel anytime</p>
                    <div className="flex justify-center items-center space-x-1 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <SafeIcon key={i} icon={FiStar} className="text-yellow-400 fill-current text-sm" />
                      ))}
                      <span className="ml-2 text-gray-400 text-sm">4.8/5 from 1,200+ users</span>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-purple-900/40 to-blue-900/40 p-6 rounded-2xl border border-purple-500/30 backdrop-blur-sm max-w-2xl mx-auto">
                  <div className="flex items-center justify-center space-x-3 mb-2">
                    <SafeIcon icon={FiUsers} className="text-purple-400 text-xl" />
                    <span className="text-purple-300 font-poppins font-semibold">Complete Onboarding Included</span>
                  </div>
                  <p className="text-gray-300 font-poppins text-center text-sm">
                    Our team personally sets up your entire system,trains you on everything,and ensures you're ready to succeed.
                  </p>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Pathway Steps */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Your <span className="clarity-gradient">Learning Journey</span>
                </h2>
                <p className="text-xl text-gray-300 font-poppins max-w-3xl mx-auto">
                  From leveraging current partnerships to building your own automated affiliate empire.
                </p>
              </motion.div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
                {pathwaySteps.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center bg-gradient-to-br from-purple-800/30 to-blue-800/30 p-6 rounded-2xl border border-purple-500/30"
                  >
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-white font-orbitron font-bold text-xl">{step.step}</span>
                    </div>
                    <h3 className="text-xl font-orbitron font-bold mb-3 text-white">{step.title}</h3>
                    <p className="text-gray-300 font-poppins text-sm">{step.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Learning Modules */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  What You'll <span className="clarity-gradient">Learn</span>
                </h2>
                <p className="text-xl text-gray-300 font-poppins max-w-3xl mx-auto">
                  Learn to use our software systems to build and automate your affiliate operations.
                </p>
              </motion.div>
              <div className="grid md:grid-cols-2 gap-8 mb-16">
                {learningModules.map((module, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                    className="bg-gradient-to-br from-purple-800/50 to-blue-800/50 p-8 rounded-2xl border border-purple-500/50 backdrop-blur-sm hover:border-purple-400/70 transition-all duration-300"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-2xl font-orbitron font-bold text-white">{module.title}</h3>
                      <span className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 px-3 py-1 rounded-full text-purple-300 font-poppins text-sm">
                        {module.tools}
                      </span>
                    </div>
                    <p className="text-gray-300 font-poppins leading-relaxed">{module.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* What's Included */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-gradient-to-r from-gray-800/40 to-gray-900/40 p-8 rounded-2xl border border-gray-700/30 backdrop-blur-sm max-w-4xl mx-auto"
              >
                <h3 className="text-2xl font-orbitron font-bold mb-6 text-center text-white">
                  Link in Bio Pro Includes
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center space-x-3 text-gray-300 font-poppins">
                      <SafeIcon icon={FiCheck} className="text-purple-400 flex-shrink-0" />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </section>

          {/* Success Stories */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Transformation <span className="clarity-gradient">Success Stories</span>
                </h2>
                <p className="text-xl text-gray-300 font-poppins">
                  From leveraging affiliates to building automated affiliate empires
                </p>
              </motion.div>
              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {successStories.map((story, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 p-8 rounded-2xl border border-gray-700/30 backdrop-blur-sm"
                  >
                    <div className="text-center mb-6">
                      <p className="text-white font-poppins font-semibold text-lg mb-1">{story.name}</p>
                      <p className="text-gray-400 font-poppins text-sm mb-4">{story.role}</p>
                      <div className="space-y-3 text-sm">
                        <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 px-3 py-2 rounded-lg">
                          <p className="text-blue-300 font-poppins">Started with: {story.current}</p>
                        </div>
                        <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30 px-3 py-2 rounded-lg">
                          <p className="text-green-300 font-poppins">Now earning: {story.outcome}</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-300 font-poppins text-sm italic mb-4 text-center">"{story.journey}"</p>
                    <div className="text-center">
                      <span className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 px-4 py-2 rounded-full text-purple-300 font-poppins text-sm">
                        Transformed in {story.timeframe}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center bg-gradient-to-r from-purple-900/40 to-blue-900/40 p-12 rounded-3xl border border-purple-500/30 backdrop-blur-sm max-w-4xl mx-auto"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Ready to <span className="clarity-gradient">Scale</span> Your Success?
                </h2>
                <p className="text-xl text-gray-300 font-poppins mb-8 max-w-2xl mx-auto">
                  Join the affiliate marketers who've upgraded their game with professional tools and complete setup.
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleProLinkClick}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 px-12 py-4 rounded-full font-poppins font-semibold text-xl glow-button"
                >
                  Get Started with Complete Setup
                </motion.button>
                <p className="text-gray-400 font-poppins text-sm mt-4">
                  Complete onboarding included • Personal training session • Cancel anytime
                </p>
              </motion.div>
            </div>
          </section>
        </div>
      );
    };

    export default LinkInBioProPage;